import React from "react";

import { ColumnType } from "components/AgGrid/constants";
import { Link } from "components/Link";

import { ModalTypes } from "constants/navigation";

import { getters } from "lib/agGrid";
import { RuleOrderRenderer } from "lib/agGrid/renderers";
import { getModalLink } from "lib/navigation";
import { GridApi, ICellRendererParams } from "ag-grid-community";

export const RuleColId = {
  APPLY_CRITERIA_FILTER_TO_SPLITS: "a287e76a-071d-453d-b59c-078d7b17c085",
  APPLY_SPLITS: "c537fced-55d7-41e9-a801-472baa3f24f5",
  BILLING_TAGS: "d022e976-a76e-43fa-8465-31188982b87a",
  CATEGORY: "95b37795-5aaa-49d4-8f0f-0466555cbe4a",
  COMMENT: "f40e6131-5a54-42eb-899f-7b3589e58e3b",
  CREATED: "cda8afc2-378d-401a-9de2-008520e03da5",
  GL_CODE: "da11dfd0-f411-46a8-9132-82c07ff570d2",
  GST_STATUS: "3bf04b62-a5f7-4a65-8d9d-c72b188e9304",
  INCLUDE_IF_ZERO: "c8f2a0ae-d9d9-4250-8465-a77db1d8e29f",
  INPUT_FIELD: "d3551e5f-be32-4f02-878e-5fcaecc70db1",
  INVOICE_LINE_ITEM_TEMPLATE: "02ced62a-2185-460b-be5b-d5b71af606f9",
  LAST_MODIFIED: "7f929fc4-71e7-4018-b105-40cd645820cb",
  MASTER_RULE_ID: "4a63d862-6c6c-4788-98c8-8fa447627d1e",
  NAME: "f0227e96-3188-457f-8b9a-98935bd6ee75",
  ORDER: "f6c9b793-e641-4631-8f52-32b622922027",
  PREREQUISITE_RULE_IDS: "b90dcb2b-062a-4c73-8dcb-2b062aec7341",
  PRICE_MULTIPLIER: "ef8b6674-fc02-4265-8912-f8bf2c1bc740",
  QUANTITY_OUTPUT_FORMAT: "a01c1e6b-dbb9-49a4-b671-1fdf2b60199e",
  QUANTITY_RAW_FORMAT: "e2ec7f3f-3fb9-4c07-b5ce-cbc95af07a53",
  RULE_BOOK_ID: "4019dbf4-4096-4774-90b7-30eb77cfc8f9",
  SEPARATE_DOCUMENT_KEY: "6acc88bd-63c6-4f65-97c9-d9ba56d79a3f",
  TAX_TYPE: "9f406190-dd07-47d3-9b91-700cd7cdc3b5",
  TITLE_TEMPLATE: "dbe46a99-58d2-4da7-85be-9f9f71818b44",
  UNIT_AMOUNT_OUTPUT_FORMAT: "14d38b28-948f-4cd3-a979-497476637f2e",
  UNIT_AMOUNT_RAW_FORMAT: "27df1983-fca8-4100-8eaa-d6d280ee1338",
};

const ruleNotAvailable = "Rule not available";
const prerequisiteRuleFormatter = (
  api: Pick<GridApi, "getRowNode" | "getCellValue">,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Array of rule IDs
  value: any[],
) => {
  const ruleIds = value || [];
  if (!value || ruleIds.length === 0) {
    return null;
  }
  return ruleIds.map(ruleId => {
    const prerequisiteNode = api.getRowNode(ruleId);
    if (!prerequisiteNode) {
      return ruleNotAvailable;
    }
    return api.getCellValue({
      colKey: RuleColId.NAME,
      rowNode: prerequisiteNode,
    });
  });
};

const PrerequisiteRuleRenderer = ({ value, api }: ICellRendererParams) => {
  if (value === null) {
    return null;
  }
  return value.map((ruleId: string) => {
    const node = api.getRowNode(ruleId);
    if (!node) {
      return (
        <span className="px-2" key={ruleId}>
          {ruleNotAvailable}
        </span>
      );
    }
    const ruleName = api.getCellValue({
      colKey: RuleColId.NAME,
      rowNode: node,
    });
    return (
      <span className="px-2" key={ruleId}>
        <Link to={getModalLink(ModalTypes.EditRule, { ruleId })}>
          {ruleName || "Rule"}
        </Link>
      </span>
    );
  });
};

export const RuleColumnDefinitions = {
  [RuleColId.NAME]: {
    colId: RuleColId.NAME,
    field: "rule.name",
    headerName: "Name",
    width: 90,
    type: ColumnType.STRING,
  },
  [RuleColId.GL_CODE]: {
    colId: RuleColId.GL_CODE,
    field: "rule.gl_code",
    headerName: "GL Code",
    width: 90,
    type: ColumnType.STRING,
    valueGetter: getters.autoFieldGetter,
    filterValueGetter: getters.autoFieldGetter,
    enableRowGroup: true,
  },
  [RuleColId.CREATED]: {
    colId: RuleColId.CREATED,
    field: "rule.created",
    headerName: "Created",
    width: 90,
    type: ColumnType.UTC_LOCAL_DATE_TIME,
  },
  [RuleColId.LAST_MODIFIED]: {
    colId: RuleColId.LAST_MODIFIED,
    field: "rule.last_modified",
    headerName: "Last Modified",
    width: 90,
    type: ColumnType.UTC_LOCAL_DATE_TIME,
  },
  [RuleColId.INVOICE_LINE_ITEM_TEMPLATE]: {
    colId: RuleColId.INVOICE_LINE_ITEM_TEMPLATE,
    field: "rule.invoice_line_item_template",
    headerName: "Grouped Title",
    width: 90,
    type: ColumnType.STRING,
  },
  [RuleColId.TITLE_TEMPLATE]: {
    colId: RuleColId.TITLE_TEMPLATE,
    field: "rule.title_template",
    headerName: "Title",
    width: 90,
    type: ColumnType.STRING,
  },
  [RuleColId.ORDER]: {
    colId: RuleColId.ORDER,
    field: "rule.order",
    headerName: "Order",
    width: 90,
    type: ColumnType.NUMERIC,
    cellRenderer: RuleOrderRenderer,
  },
  [RuleColId.BILLING_TAGS]: {
    colId: RuleColId.BILLING_TAGS,
    field: "rule.parsedContent.billing_tags",
    headerName: "Tags",
    width: 90,
    type: ColumnType.STRING,
    enableRowGroup: true,
  },
  [RuleColId.CATEGORY]: {
    colId: RuleColId.CATEGORY,
    field: "rule.parsedContent.category",
    headerName: "Category",
    width: 90,
    type: ColumnType.STRING,
    enableRowGroup: true,
  },
  [RuleColId.INCLUDE_IF_ZERO]: {
    colId: RuleColId.INCLUDE_IF_ZERO,
    field: "rule.parsedContent.include_if_zero",
    headerName: "Include If Zero?",
    width: 90,
    type: ColumnType.BOOLEAN,
  },
  [RuleColId.INPUT_FIELD]: {
    colId: RuleColId.INPUT_FIELD,
    field: "basedOn",
    headerName: "Based On",
    width: 90,
    type: ColumnType.STRING,
    valueGetter: getters.ruleInputField,
    enableRowGroup: true,
  },
  [RuleColId.TAX_TYPE]: {
    colId: RuleColId.TAX_TYPE,
    field: "rule.parsedContent.tax_type",
    headerName: "Tax Type",
    width: 90,
    type: ColumnType.STRING,
    valueGetter: getters.autoFieldGetter,
    filterValueGetter: getters.autoFieldGetter,
    enableRowGroup: true,
  },
  [RuleColId.PRICE_MULTIPLIER]: {
    colId: RuleColId.PRICE_MULTIPLIER,
    field: "rule.parsedContent.price_multiplier",
    headerName: "Price Multiplier",
    width: 90,
    type: ColumnType.NUMERIC,
  },
  [RuleColId.PREREQUISITE_RULE_IDS]: {
    colId: RuleColId.PREREQUISITE_RULE_IDS,
    field: "rule.prerequisite_rule_ids",
    headerName: "Generate From",
    enableRowGroup: true,
    filterValueGetter: ({ api, getValue, colDef }) =>
      prerequisiteRuleFormatter(api, getValue(colDef.colId)),
    valueFormatter: ({ api, value }) => prerequisiteRuleFormatter(api, value),
    cellRenderer: PrerequisiteRuleRenderer,
  },
  [RuleColId.SEPARATE_DOCUMENT_KEY]: {
    colId: RuleColId.SEPARATE_DOCUMENT_KEY,
    field: "rule.separate_document_key",
    headerName: "Separate Document Key",
    width: 90,
    type: ColumnType.STRING,
    enableRowGroup: true,
  },
  [RuleColId.GST_STATUS]: {
    colId: RuleColId.GST_STATUS,
    field: "rule.parsedContent.gst_status",
    headerName: "GST Status",
    enableRowGroup: true,
  },
  [RuleColId.UNIT_AMOUNT_OUTPUT_FORMAT]: {
    colId: RuleColId.UNIT_AMOUNT_OUTPUT_FORMAT,
    field: "rule.unit_amount_output_format",
    headerName: "Unit Amount Output Format",
    type: ColumnType.STRING,
    enableRowGroup: true,
  },
  [RuleColId.UNIT_AMOUNT_RAW_FORMAT]: {
    colId: RuleColId.UNIT_AMOUNT_RAW_FORMAT,
    field: "rule.unit_amount_raw_format",
    headerName: "Unit Amount Raw Format",
    type: ColumnType.STRING,
    enableRowGroup: true,
  },
  [RuleColId.QUANTITY_OUTPUT_FORMAT]: {
    colId: RuleColId.QUANTITY_OUTPUT_FORMAT,
    field: "rule.quantity_output_format",
    headerName: "Quantity Output Format",
    type: ColumnType.STRING,
    enableRowGroup: true,
  },
  [RuleColId.QUANTITY_RAW_FORMAT]: {
    colId: RuleColId.QUANTITY_RAW_FORMAT,
    field: "rule.quantity_raw_format",
    headerName: "Quantity Raw Format",
    type: ColumnType.STRING,
    enableRowGroup: true,
  },
  [RuleColId.APPLY_SPLITS]: {
    colId: RuleColId.APPLY_SPLITS,
    field: "rule.apply_splits",
    headerName: "Apply Splits",
    width: 90,
    type: ColumnType.BOOLEAN,
    enableRowGroup: true,
  },
  [RuleColId.APPLY_CRITERIA_FILTER_TO_SPLITS]: {
    colId: RuleColId.APPLY_CRITERIA_FILTER_TO_SPLITS,
    field: "rule.apply_criteria_filter_to_splits",
    headerName: "Apply Splits to Filter",
    width: 90,
    type: ColumnType.BOOLEAN,
    enableRowGroup: true,
  },
};
